import {
  OnInit,
  Component,
  Input,
  OnDestroy,
  HostListener,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  inject,
  input,
  output,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { isEqual } from 'lodash';
import { ReplaySubject } from 'rxjs';
import { takeUntil, filter, map } from 'rxjs/operators';

import { LaunchDarklyFlags } from 'app/core/services/launch-darkly/launch-darkly.types';
import { coreSelectors, getFeatureFlagObservable } from 'app/core/state';
import { ModalErrorComponent, ModalErrorData } from 'app/shared/modal-error/modal-error.component';
import { Loggable } from 'app/utils/logging/loggable';
import { notNil } from 'app/utils/stream-util';
import User from 'entity/User';

import { DeviceService } from '../services/device/device.service';
import { socialAppActions, socialAppSelectors } from '../state/app';
import { selectors, SimpleMediaDeviceInfo } from '../state/media';
import { PresenterVideoQuality, PresenterVideoQualityWithLabel, PresenterVideoQualityWithLabels } from '../types/media';

import { DeviceType, MultimediaType, DeviceGroup } from './multimedia-dropdown.types';

@Component({
  selector: 'app-multimedia-dropdown',
  templateUrl: './multimedia-dropdown.component.html',
  styleUrls: ['./multimedia-dropdown.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MultimediaDropdownComponent extends Loggable implements OnInit, OnDestroy {
  private deviceService = inject(DeviceService);
  private store = inject(Store);
  private dialog = inject(MatDialog);
  private changeDetectorRef = inject(ChangeDetectorRef);

  // TODO: Skipped for migration because:
  //  Your application code writes to the input. This prevents migration.
  @Input() public type: MultimediaType = MultimediaType.AUDIO;
  public readonly isVideoEnabled = input(true);
  public readonly isMicEnabled = input(true);

  //Event to tell the parent if the menu is up, in wchich case it can do some z-index change
  //to prevent 'Capture Debug Data' button to stand on top of the opened menu.
  //If the menu is closed the button's z-index must be higher than the menu's.
  public readonly showDropdownEvent = output<boolean>();

  public user?: User;
  public devicesGroup: Map<DeviceType, DeviceGroup> = new Map<DeviceType, DeviceGroup>();
  public speakers: SimpleMediaDeviceInfo[] = [];
  public isChangingDevice = false;

  private _showDropdown = false;

  private destroyed$ = new ReplaySubject<boolean>(1);
  public PresenterVideoQuality = PresenterVideoQuality;
  public presenterVideoQualityWithLabels: PresenterVideoQualityWithLabel[];
  public currentQuality = PresenterVideoQuality.QUALITY_STANDARD;
  public isPresenter = false;
  public isPresentationActive = false;
  public hasHdPresenter = false;
  public hd_presenter_1080p = false;
  public videoQualityLevelAvailable: 'hd_presenter_1080p' | 'hd_presenter' | 'standard' = 'standard';

  public constructor() {
    super();
    this.setDisplayName('MultimediaDropdownComponent');

    this.store
      .select(socialAppSelectors.getPresenterQualityOverride)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((currentQuality) => {
        this.currentQuality = currentQuality ? currentQuality : PresenterVideoQuality.QUALITY_STANDARD;
        this.changeDetectorRef.markForCheck();
      });

    this.store
      .select(coreSelectors.getUserEntity)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((user) => {
        this.user = user;
        this.changeDetectorRef.markForCheck();
      });

    this.store
      .select(socialAppSelectors.isUserPresenting)
      .pipe(filter(notNil), takeUntil(this.destroyed$))
      .subscribe((isUserPresenting: boolean) => {
        this.isPresenter = isUserPresenting;
        this.changeDetectorRef.markForCheck();
      });

    this.store
      .select(socialAppSelectors.isPresentationActive)
      .pipe(filter(notNil), takeUntil(this.destroyed$))
      .subscribe((isPresentationActive: boolean) => {
        this.isPresentationActive = isPresentationActive;
        this.changeDetectorRef.markForCheck();
      });

    getFeatureFlagObservable(this.store, LaunchDarklyFlags.hd_presenter)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((hd_presenter) => {
        this.hasHdPresenter = hd_presenter;
        this.changeDetectorRef.markForCheck();
      });

    getFeatureFlagObservable(this.store, LaunchDarklyFlags.hd_presenter_1080p)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((hd_presenter_1080p) => {
        this.hd_presenter_1080p = hd_presenter_1080p;
        this.changeDetectorRef.markForCheck();
      });

    this.presenterVideoQualityWithLabels = this.filterVideoQualityOptions();
  }

  public ngOnInit(): void {
    this.store
      .select(selectors.getCameraState)
      .pipe(
        filter(() => this.type === MultimediaType.CAMERA),
        map(({ cameras: devices, selectedCamera: selectedDevice }) => ({
          devices,
          selectedDevice,
          type: DeviceType.CAMERA,
        })),
        takeUntil(this.destroyed$),
      )
      .subscribe(this.updateDevices.bind(this));

    this.store
      .select(selectors.getMicState)
      .pipe(
        filter(() => this.type === MultimediaType.AUDIO),
        map(({ mics: devices, selectedMic: selectedDevice }) => ({
          devices,
          selectedDevice,
          type: DeviceType.MICROPHONE,
        })),
        takeUntil(this.destroyed$),
      )
      .subscribe(this.updateDevices.bind(this));

    this.store
      .select(selectors.getSpeakerState)
      .pipe(
        filter(() => this.type === MultimediaType.AUDIO),
        map(({ selectedSpeaker: selectedDevice, speakers: devices }) => ({
          devices,
          selectedDevice,
          type: DeviceType.SPEAKER,
        })),
        takeUntil(this.destroyed$),
      )
      .subscribe(this.updateDevices.bind(this));
  }

  private filterVideoQualityOptions(): PresenterVideoQualityWithLabel[] {
    return PresenterVideoQualityWithLabels.filter((option) => {
      if (this.hd_presenter_1080p) {
        this.videoQualityLevelAvailable = 'hd_presenter_1080p';
        return true;
      } else if (this.hasHdPresenter) {
        this.videoQualityLevelAvailable = 'hd_presenter';
        return option.key !== PresenterVideoQuality.QUALITY_1080P;
      }
      this.videoQualityLevelAvailable = 'standard';
      return option.key !== PresenterVideoQuality.QUALITY_1080P && option.key !== PresenterVideoQuality.QUALITY_720P;
    });
  }

  private updateDevices({
    devices,
    selectedDevice,
    type,
  }: {
    type: DeviceType;
    selectedDevice: SimpleMediaDeviceInfo | null;
    devices: SimpleMediaDeviceInfo[];
  }): void {
    this.devicesGroup.set(type, { selectedDevice, devices });
    this.changeDetectorRef.markForCheck();
  }

  public ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  public get showDropdown(): boolean {
    return this._showDropdown;
  }

  public set showDropdown(show: boolean) {
    this._showDropdown = show;
    this.showDropdownEvent.emit(show);
    // Set window listeners to catch clicks outside of the dropdown
  }

  public toggleDropdown(): void {
    this.showDropdown = !this.showDropdown;
  }

  public dropdownClicked(e: Event): void {
    // Stop propagation so the dropdown doesn't close when clicked
    e.stopPropagation();
  }

  public deviceIsEqual(device1: SimpleMediaDeviceInfo, device2: SimpleMediaDeviceInfo): boolean {
    return isEqual(device1, device2);
  }

  private setSelectedDeviceType(deviceType: DeviceType): string {
    switch (deviceType) {
      case DeviceType.CAMERA: {
        return 'videoinput';
      }
      case DeviceType.MICROPHONE: {
        return 'audioinput';
      }
      case DeviceType.SPEAKER: {
        return 'audiooutput';
      }
    }
  }

  public isDeviceSelected(deviceType: DeviceType, device: SimpleMediaDeviceInfo): boolean {
    const group = this.devicesGroup.get(deviceType);
    if (!group) {
      return false;
    }
    return device.deviceId === group.selectedDevice?.deviceId;
  }

  public async optionChanged(deviceType: DeviceType, device: SimpleMediaDeviceInfo): Promise<void> {
    const type = this.setSelectedDeviceType(deviceType);

    if (this.isDeviceSelected(deviceType, device)) {
      return;
    }

    this.isChangingDevice = true;

    try {
      await this.deviceService.handleDeviceChange(device, type);
    } catch (error) {
      this.error('Failed to switch device', { device, error: (error as object).toString() });
      const message = {
        title: 'Oops!',
        text: `The device ${device.label} is not available. Please check if another application is using it.`,
      };
      this.dialog.open<ModalErrorComponent, ModalErrorData>(ModalErrorComponent, {
        data: message,
      });
    } finally {
      this.isChangingDevice = false;
    }
  }

  public canOverrideQuality(): boolean {
    return this.type === MultimediaType.CAMERA && this.isPresenter && this.isPresentationActive && this.hasHdPresenter;
  }

  public onQualityOverride(key: string) {
    const newQualityKey = Object.keys(PresenterVideoQuality).find(
      (k) => PresenterVideoQuality[k as keyof typeof PresenterVideoQuality] === key,
    );
    const newQuality = PresenterVideoQuality[newQualityKey as keyof typeof PresenterVideoQuality];
    this.logUserAction('QualityOverrideDropdown', 'click', { newQuality });
    this.store.dispatch(socialAppActions.OVERRIDE_QUALITY({ quality: newQuality }));
    this.currentQuality = newQuality;
  }

  /**
   * Handle clicks on the window, to close the dropdown when the user clicks anywhere else on the window outside
   * of the dropdown
   **/
  @HostListener('window:click', ['$event'])
  @HostListener('window:touchstart', ['$event'])
  private windowClickListener = (e: MouseEvent | TouchEvent) => {
    const { classList } = e.target as HTMLElement;
    // Don't hide the dropdown if the toggle is being clicked
    if (classList.contains(this.type) && classList.contains('dropdown-toggle')) {
      return;
    }
    this.showDropdown = false;
  };
}
